import { toast } from "@valenzinsights/core-component";
import { backendApiClient } from "@valenzinsights/data-access";
import { IApiClientResponse } from "@valenzinsights/utils";
import { IDocumentData, IDocumentIngestStep, IWorkspaceData } from "./apis.types";

export const getWorkpaceFolders = async (
	showLoader: () => void,
	hideLoader: () => void,
) => {
	showLoader();
	try {
		const res = await backendApiClient().get("/workspaces", {
			enableLoader: true,
		});
		return res.data as IWorkspaceData[];
	} catch (err) {
		console.error(err);
		return [];
	} finally {
		hideLoader();
	}
};

export const addWorkspace = async (
	addUrl: string,
	data: { name: string },
	showLoader: () => void,
	getWorkpaceFolders: () => void,
) => {
	showLoader();
	try {
		const res = await backendApiClient().post(addUrl, data, {
			enableLoader: true,
		});
		const resdata = res.data as { id: string };

		if (resdata && resdata.id) {
			toast.success("Workspace Added Successfully");
		} else {
			toast.error("Workspace Addition Failed.");
		}
		return resdata;
	} catch (err) {
		toast.error("Workspace addition failed. Please try again after some time.");
		throw err;
	} finally {
		getWorkpaceFolders();
	}
};

export const deleteWorkspace = async (
	deleteUrl: string,
	showLoader: () => void,
	getWorkpaceFolders: () => void,
) => {
	showLoader();
	try {
		const res = await backendApiClient().delete(deleteUrl, {
			enableLoader: true,
		});
		const data = res.data as { status: boolean; message: string };

		if (data.status === true) {
			toast.success(data.message);
		} else {
			toast.error(data.message);
		}
		return data;
	} catch (err) {
		toast.error("Workspace deletion failed. Please try again after some time.");
		throw err;
	} finally {
		getWorkpaceFolders();
	}
};

// Document page
export const getWorkpaceItems = async (
	docId: number,
	showLoader: () => void,
	hideLoader: () => void,
) => {
	showLoader();
	try {
		const res = await backendApiClient().get<IDocumentData>(
			`/workspace/items?WorkspaceId=${docId}&PageSize=100&PageNumber=1`,
			{ enableLoader: true },
		);
		return Array.isArray(res.data) ? res.data : false;
	} catch (err) {
		console.error(err);
		return [];
	} finally {
		hideLoader();
	}
};

export const deleteWorkspaceItems = async (
	deleteUrl: string,
	workspaceId: number,
	showLoader: () => void,
	getWorkpaceItems: ((arg0: number) => any) | undefined,
) => {
	showLoader();
	try {
		const res = await backendApiClient().delete(deleteUrl, {
			enableLoader: true,
		});
		const data = res.data as Boolean;

		if (data === true) {
			toast.success("Document Deleted Successfully");
		} else {
			toast.error("Document Deletion Failed.");
		}
		return data;
	} catch (err) {
		toast.error("Document deletion failed. Please try again after some time.");
		throw err;
	} finally {
		workspaceId && getWorkpaceItems && getWorkpaceItems(workspaceId);
	}
};

export const downloadWorkspaceItem = async (
	downloadUrl: string,
	documentName: string,
	showLoader: () => void,
	hideLoader: () => void,
) => {
	try {
		showLoader();

		backendApiClient()
			.get<Blob>(downloadUrl, { enableLoader: true, responseType: "blob" })
			.then((response: IApiClientResponse<Blob>) => {
				const downloadUrl = window.URL.createObjectURL(new Blob([response.data]));
				const link = document.createElement("a");
				link.href = downloadUrl;
				link.setAttribute("download", `${documentName}`);
				document.body.appendChild(link);
				link.click();
				document.body.removeChild(link);
				window.URL.revokeObjectURL(downloadUrl);
			});
	} catch (err) {
		console.error(err);
		toast.error("Document download failed. Please try again after some time.");
		throw err;
	} finally {
		hideLoader();
	}
};

export const valanzUploadValidation = async (
	valanzUploadFromData: any,
	showLoader: () => void,
	hideLoader: () => void,
): Promise<boolean> => {
	showLoader();
	try {
		const response = await backendApiClient().post<boolean>(
			"/workspaceitem-validation",
			valanzUploadFromData,
		);
		return response?.data ?? false;
	} catch (error) {
		console.error("Error in valanzUploadValidation");
		return false;
	} finally {
		hideLoader();
	}
};

export const onDefaultFileUpload = async (
	addFileUrl: string,
	addfileData: any,
	showLoader: () => void,
	hideLoader: () => void,
) => {
	const formData = new FormData();
	if (addfileData?.files && addfileData.files[0]?.data) {
		formData.append("file", addfileData?.files[0]?.data);
	}
	formData.append("tags", JSON.stringify(addfileData?.tags || []));
	formData.append("workspaceId", addfileData?.workspaceId || "");

	const response = await addFileItem(
		addFileUrl,
		formData,
		showLoader,
		hideLoader,
	);
	if (response && response.documentId) {
		return { id: response.documentId, name: response.fileName };
	}
	return undefined;
};

export const addFileItem = async (
	addFileUrl: string,
	data: any,
	showLoader: () => void,
	hideLoader: () => void,
) => {
	showLoader();
	try {
		const config = {
			"Content-Type": "multipart/form-data",
		};
		const res = await backendApiClient().post(addFileUrl, data, config);
		const resdata = res.data as any;
		if (resdata && resdata.documentId) {
			toast.success("Document Added Successfully");
		} else {
			toast.error("Document Addition Failed.");
		}
		return resdata;
	} catch (err) {
		throw err;
	} finally {
		hideLoader();
	}
};

export const getDocumentUrl = async (documentId: string) => {
	try {
		const res = await backendApiClient().get(
			`/workspace/item/libraryurl?itemId=${documentId}`,
		);
		if (typeof res.data === "object" && res.data !== null && "url" in res.data) {
			return (res.data as { url: string }).url;
		}
		return null;
	} catch (err) {
		console.error(err);
		return null;
	} finally {
	}
};

export const getCitationUrl = async (documentName: string) => {
	try {
		const res = await backendApiClient().get(
			`/document/view/citationUrl?documentName=${documentName}`,
		);
		return res.data as string;
	} catch (err) {
		console.error(err);
		return null;
	} finally {
	}
};

export const getChatStatus = async (
	showLoader: () => void,
	hideLoader: () => void,
) => {
	showLoader();
	try {
		const res = await backendApiClient().get(
			"/chatstatus",
			{ enableLoader: true },
		);
		return res.data;
	} catch (err) {
		console.error(err);
		toast.error("Failed to fetch chat status. Please try again after some time.");
		return null;
	} finally {
		hideLoader();
	}
};

// Document page
export const getDocumentIngestDetails = async (docId: number) => {
	try {
		const res = await backendApiClient().get<IDocumentIngestStep>(
			`/document/ingestiondetails/${docId}`,
			{ enableLoader: true },
		);
		return Array.isArray(res.data) ? res.data : false;
	} catch (err) {
		console.error(err);
		return [];
	}
};
